import { css } from "@emotion/css";
import { Chip, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { CompliancePolicyVersionResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import React from "react";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import DateFormatter from "~/utils/DateFormatter/index";
interface PolicyVersionsTabProps {
    versions: CompliancePolicyVersionResource[];
    openActivationStatusDialog: (version: CompliancePolicyVersionResource) => void;
}
export function PolicyVersionsTab({ versions, openActivationStatusDialog }: PolicyVersionsTabProps) {
    if (versions.length === 0) {
        return (<OnboardingPage title="No published versions found for this policy" intro={<div>
                        <div>Only active published versions will be applied and made visible here.</div>
                        <div>
                            To get started on publishing a version, select <b>Publish</b> in the top right corner.
                        </div>
                    </div>} learnMore={null}/>);
    }
    return (<div>
            <SimpleDataTable columns={[
            {
                title: "Version",
                render: (data) => (<div className={styles.versionContainer}>
                                <b>{data.Version}</b>
                                {data.IsActive ? <Chip backgroundColor={themeTokens.color.badge.background.info} label={"Active"} labelColor={themeTokens.color.badge.text.primary} noMargin={true}/> : null}
                            </div>),
                columnSize: "medium",
            },
            { title: "Git reference", render: (data) => <GitRefChip vcsRef={{ GitRef: data.GitRef, GitCommit: data.GitCommit }} showCommit={true}/>, columnSize: "medium" },
            { title: "Published", render: (data) => DateFormatter.dateToLongFormat(data.PublishedDate) },
            { title: "", columnSize: "x-small", render: (data) => <PolicyVersionOverflowMenu version={data} openActivationStatusDialog={openActivationStatusDialog}/> },
        ]} data={versions} getRowKey={(data) => data.Version}/>
        </div>);
}
interface PolicyVersionOverflowMenuProps {
    version: CompliancePolicyVersionResource;
    openActivationStatusDialog: (version: CompliancePolicyVersionResource) => void;
}
function PolicyVersionOverflowMenu({ version, openActivationStatusDialog }: PolicyVersionOverflowMenuProps) {
    const item = OverflowMenuItems.item("Change activation status", () => openActivationStatusDialog(version), { permission: Permission.PlatformHubEdit }, "Change published policy activation status");
    const overFlowActions: Array<MenuItem | MenuItem[]> = [item];
    return <OverflowMenu menuItems={overFlowActions}/>;
}
const styles = {
    versionContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
    }),
    titleContainer: css({
        flex: "1",
        display: "flex",
        gap: space[12],
        alignItems: "center",
        overflow: "hidden",
    }),
    title: css({
        all: "unset",
        font: text.heading.large,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
};
